import type { TFunction } from "i18next";
import { MODAL, ModalType } from "redux/slices/modal";

export type ModalHeader = { title?: string; subtitle?: string };

type HeaderResolver = (t: TFunction, data: any) => ModalHeader;

const asString = (value: unknown): string | undefined =>
  value == null ? undefined : String(value);

/* The words for each modal's header, as in yelo-usernext's modalHeaders —
   limited to the modals this app has. A modal absent from the map gets the
   close button alone. */
const headers: Partial<Record<keyof typeof MODAL, HeaderResolver>> = {
  /* ---- payment ---- */
  ADD_STRIPE_PAYMENT: (t, data) => ({
    title: asString(
      data?.context === "checkout" ? t("addNewCard") : t("Payment method")
    ),
    subtitle: asString(t("add_card_checkout_hint")),
  }),
  ADD_PAYMENT: (t) => ({ title: asString(t("Payment method")) }),
  PAYMENT_CARDS: (t) => ({
    title: asString(t("selectPaymentMethod")),
    subtitle: asString(t("checkout_payment_method_hint")),
  }),
  APPLY_COUPON: (t) => ({
    title: asString(t("addCouponTitle", "Add coupon")),
    subtitle: asString(
      t("addCouponHint", "Enter a code to apply it to this booking.")
    ),
  }),

  /* ---- booking ---- */
  CHILDREN_SEATS: (t) => ({ title: asString(t("howManyChildren")) }),
  UPDATE_BOOKING: (t) => ({
    title: asString(t("updateBooking", "Update booking")),
  }),
  HOURLY_DESTINATION: (t) => ({
    title: asString(t("hourly_destination_title", "Where are you going?")),
    subtitle: asString(
      t(
        "hourly_destination_hint",
        "The hours you book have to cover the drive to your drop-off. We need that place so we can check the drive time."
      )
    ),
  }),

  /* ---- account ---- */
  UPDATE_PROFILE: (t) => ({
    title: asString(t("updateProfile", "Update profile")),
  }),
  BILLING_ADDRESS: (t) => ({ title: asString(t("billingAddress")) }),
  UPLOAD_DOCUMENT: (t) => ({ title: asString(t("upload")) }),
  DELETE_USER: (t) => ({ title: asString(t("deleteAccount", "Delete account")) }),
  REPORT_A_PROBLEM: (t) => ({
    title: asString(t("reportAProblem", "Report a problem")),
  }),
  EDIT_ADDRESS: (t) => ({ title: asString(t("editAddress", "Edit address")) }),

  /* ---- confirm dialogs: their words arrive with the payload ---- */
  CONFIRMATION_FORM: (t, data) => ({
    title: data?.heading
      ? asString(t(data.heading))
      : asString(t("confirm", "Confirm")),
    /* A cancel with a refund summary lays it out in the body instead. */
    subtitle: data?.refund ? "" : asString(data?.message),
  }),
  /* The wallet's remove/make-default dialog; its sentence names the
     account, which is why it is composed here. */
  CONFIRMATION_MODAL: (t, data) => {
    const label =
      data?.accountLabel ||
      (data?.payment_type === "s" ? t("card") : t("payPalAccount"));
    const isRemove = data?.type === "remove";
    return {
      title: asString(isRemove ? t("removePayment") : t("makeDefault")),
      subtitle: isRemove
        ? `${t("Do you really want to remove this")} ${label}?`
        : `${t("Do you really want to make this")} ${label} ${t("default")}?`,
    };
  },
};

export function getModalHeader(
  type: ModalType,
  t: TFunction,
  data: any
): ModalHeader {
  if (!type) return {};
  const resolve = headers[type as keyof typeof MODAL];
  return resolve ? resolve(t, data) : {};
}
